/**
 * Row copy shared by the add-players panel's Suggested tab and its global
 * search results (SE-5490).
 *
 * Both surfaces render the same row, but they are fed by two different queries
 * with two different generated types (`PipelineSuggestedPlayerFragmentFragment`
 * vs `PlayerSearchPlayerFragment`). These helpers take a STRUCTURAL shape so
 * one formatter serves both — a row that read differently depending on which
 * pool surfaced the player would be a bug, not a feature.
 *
 * Every field involved is nullable in the schema, so each segment is dropped
 * rather than rendered as a placeholder.
 * @module features/player-kanban/utils/addPlayersRowMeta
 */

/** The minimum a player must expose to render an add-players row. */
export interface AddPlayersRowPlayer {
  readonly name?: string | null;
  readonly knownName?: string | null;
  readonly teamName?: string | null;
  readonly team?: { readonly name?: string | null } | null;
  readonly positions?:
    | readonly ({ readonly abbreviation?: string | null } | null)[]
    | null;
  readonly bioData?: {
    readonly age?: number | null;
    readonly gpr?: number | null;
  } | null;
}

/** Separator between meta segments. */
const SEPARATOR = " · ";

/**
 * The name to show for a player.
 * @param player - The player.
 * @returns `knownName` when present (that is the name scouts use), else `name`,
 * else a neutral placeholder so a row never renders blank.
 */
export const formatCandidateName = (player: AddPlayersRowPlayer): string => {
  const known = player.knownName?.trim();
  if (known) return known;
  const full = player.name?.trim();
  return full || "Unknown player";
};

/**
 * The suggestion card's secondary line: club · position (PC1, e.g.
 * "FC Barcelona · CM").
 *
 * GPR remains ROSTER METADATA wherever it appears — the same number every
 * player row in the app shows. It is not, and must not be presented as, a
 * stated reason for a recommendation; reasons come only from
 * `SuggestionReasonCategory`.
 * @param player - The player.
 * @returns The joined line, or an empty string when nothing is known.
 */
export const formatCandidateMeta = (player: AddPlayersRowPlayer): string => {
  const club = player.team?.name?.trim() || player.teamName?.trim();
  const position = formatCandidatePosition(player);

  return [club, position || undefined]
    .filter((segment): segment is string => Boolean(segment))
    .join(SEPARATOR);
};

/**
 * The search row's secondary line: positions · club (PC2, e.g.
 * "RW, LW · Arsenal"). Every position abbreviation is listed, comma-joined —
 * the compact row carries no position chip.
 * @param player - The player.
 * @returns The joined line, or an empty string when nothing is known.
 */
export const formatSearchMeta = (player: AddPlayersRowPlayer): string => {
  const positions = (player.positions ?? [])
    .flatMap(entry => {
      const abbreviation = entry?.abbreviation?.trim();
      return abbreviation ? [abbreviation] : [];
    })
    .join(", ");
  const club = player.team?.name?.trim() || player.teamName?.trim();

  return [positions || undefined, club]
    .filter((segment): segment is string => Boolean(segment))
    .join(SEPARATOR);
};

/**
 * The watchlist row's secondary line: position · club · age (PC5, e.g.
 * "CM · Arsenal · 25y"). The SE-6554 contract-months segment is gone — the
 * PC5 frame does not draw it.
 * @param player - The player.
 * @returns The joined line, or an empty string when nothing is known.
 */
export const formatWatchlistMeta = (player: AddPlayersRowPlayer): string => {
  const position = formatCandidatePosition(player);
  const club = player.team?.name?.trim() || player.teamName?.trim();
  const { age } = player.bioData ?? {};

  return [
    position || undefined,
    club,
    typeof age === "number" ? `${age}y` : undefined,
  ]
    .filter((segment): segment is string => Boolean(segment))
    .join(SEPARATOR);
};

/**
 * The My Roster row's secondary line: position · age · squad status (SE-6952,
 * PC6, e.g. "CAM · 27y · Starting XI" — rendered here as "CAM · 27y · First
 * Team", because "First Team" is what the data can state truthfully: the
 * schema carries `isAcademy` and nothing about starting-XI membership, so the
 * design's example has no source field and is not invented.
 * @param player - The player.
 * @param isAcademy - Whether the player is an academy player.
 * @returns The joined line, or an empty string when nothing is known.
 */
export const formatRosterMeta = (
  player: AddPlayersRowPlayer,
  isAcademy: boolean
): string => {
  const position = formatCandidatePosition(player);
  const { age } = player.bioData ?? {};

  return [
    position || undefined,
    typeof age === "number" ? `${age}y` : undefined,
    isAcademy ? "Academy" : "First Team",
  ]
    .filter((segment): segment is string => Boolean(segment))
    .join(SEPARATOR);
};

/**
 * The position abbreviation shown as the row's chip (Figma 80:15690).
 * @param player - The player.
 * @returns The abbreviation, or an empty string when the player has no
 * position — the chip is then omitted rather than rendered blank.
 */
export const formatCandidatePosition = (
  player: AddPlayersRowPlayer
): string => {
  const position = player.positions?.find(
    entry => entry?.abbreviation
  )?.abbreviation;
  return position?.trim() || "";
};

/**
 * The GPR score for the row's tone pill (Figma 80:15690).
 * @param player - The player.
 * @returns The score as a string, or null when unscored — the pill is then
 * omitted, never filled with a zero or a dash.
 */
export const formatCandidateGpr = (
  player: AddPlayersRowPlayer
): string | null => {
  const { gpr } = player.bioData ?? {};
  return typeof gpr === "number" ? String(gpr) : null;
};
